import CommonModal from "component/Common/CommonModal";
import { ChevronLeftIcon, ArrowUpRightIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import { images } from "helpers/images";
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import styles from "./styles.module.scss";
import feedbackQuestions from "assets/jsons/feedback_questions.json";
import { Radio } from "@mui/material";
import { apiMethods, endPoints } from "service/endpoints";
import { request } from "service/request";
import RatingChoices from "component/Dashboard/FeedbackNew/RatingChoices";

const Feedback = ({ showFeedback, handleCloseFeedback }) => {
  const source = useRef(axios.CancelToken.source());
  const portalSteps = feedbackQuestions?.order;
  const ratingChoices = [
    { label: "Bad", emoji: "\u{1F614}" },
    { label: "Poor", emoji: "\u{1F641}" },
    { label: "Average", emoji: "\u{1F610}" },
    { label: "Good", emoji: "\u{1F603}" },
    { label: "Excellent", emoji: "\u{1F929}" },
  ];
  const [feedbackStep, setFeedbackStep] = useState(null);
  const [feedbackData, setFeedbackData] = useState({});
  const [currentPortalStep, setCurrentPortalStep] = useState(null);
  const [feedbackOtherValue, setFeedbackOthervalue] = useState(null);

  useEffect(() => {
    setFeedbackStep("Welcome");
    setCurrentPortalStep("ratings");
  }, []);

  useEffect(() => {
    if (feedbackStep === "ThankYou") {
      submitFeedback("PROVIDED");
    }
  }, [feedbackStep]);

  const submitFeedback = async (status) => {
    let feedbackParams = { status: status };
    if (status === "PROVIDED") {
      let flattenedFeedbackData = Object.assign({}, ...Object.values(feedbackData));
      feedbackParams["feedback"] = flattenedFeedbackData;
    }
    try {
      await request({
        url: endPoints.feedback.SUBMIT_FEEDBACK,
        method: apiMethods.POST,
        isLoader: false,
        showToast: false,
        cancelToken: source.current.token,
        data: feedbackParams,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleFeedbackData = (primaryKey, value) => {
    setFeedbackData((prevData) => {
      const updateFeedbackData = { ...prevData };
      updateFeedbackData[primaryKey] = value;
      return updateFeedbackData;
    });
  };

  const handleFeedbackPopupClose = () => {
    setFeedbackData({});
    handleCloseFeedback();
  };

  const ProgressBar = () => {
    return (
      <div className="grid grid-cols-4 gap-2 min-h-2 w-full mb-4">
        {portalSteps.map((_, index) => (
          <div
            key={index}
            className={`rounded-lg ${
              portalSteps.indexOf(currentPortalStep) >= index ? "bg-[#559822]" : "bg-[#F0F0F0]"
            }`}
          ></div>
        ))}
      </div>
    );
  };

  const FeedbackRatings = ({ ratingValues, handleRatingValues }) => {
    return (
      <div className="flex flex-col gap-6 pt-2 pb-16">
        {feedbackQuestions.ratings.map((rating, index) => {
          return (
            <div key={index} className="flex flex-col items-start gap-2">
              <p className="text-lg font-extrabold">{rating.question}</p>
              <RatingChoices
                ratingChoices={ratingChoices}
                selectedRating={ratingValues[rating.key]}
                onSelect={(ratingValue) => handleRatingValues(rating.key, ratingValue)}
              />
            </div>
          );
        })}
      </div>
    );
  };
  const FeedbackOptions = ({
    optionValues,
    handleOptionValues,
    otherValue,
    handleOtherValueChange,
  }) => {
    return (
      <div className="flex flex-col gap-6 pt-2 pb-16">
        {feedbackQuestions.options.map((feedbackOption, index) => {
          return (
            <div key={index} className="flex flex-col items-start gap-2">
              <p className="text-lg font-extrabold">{feedbackOption.question}</p>
              <div className="grid grid-cols-1 gap-3">
                {feedbackOption?.options?.map((option, index) => {
                  return (
                    <ChoiceContainer
                      index={index}
                      selectedKey={feedbackOption.key}
                      choiceValue={option}
                      selectedChoiceValues={optionValues}
                      handleSelectChoice={handleOptionValues}
                    />
                  );
                })}
                {optionValues[feedbackOption?.key] === "Any other" && (
                  <input
                    className="!py-3 !px-3 border border-[#D9D9D9] rounded text-sm font-medium w-full focus:outline-0"
                    value={otherValue}
                    onChange={handleOtherValueChange}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };
  const FeedbackTexts = ({ textValues, handleTextValues }) => {
    return (
      <div className="flex flex-col gap-6 pt-2 pb-16">
        {feedbackQuestions.texts.map((feedbackText, index) => {
          return (
            <div key={index} className="flex flex-col items-start gap-2">
              <div className="flex flex-col gap-1">
                <p className="text-lg font-extrabold">{feedbackText.question}</p>
                {feedbackText?.subText && (
                  <p className="text-sm text-[#5E5E5E]">{feedbackText.subText}</p>
                )}
              </div>
              <textarea
                className="!py-2 !px-2 border border-[#D9D9D9] bg-[#F5F5F5] rounded text-sm font-medium w-full min-h-20 focus:outline-0"
                value={textValues[feedbackText.key]}
                placeholder="Share your experience"
                maxLength={4000}
                onChange={(e) => handleTextValues(feedbackText.key, e.target.value)}
              />
            </div>
          );
        })}
      </div>
    );
  };
  const FeedbackAssociation = ({ associationValues, handleAssociationValues }) => {
    const [postCheckValue, setPostCheckValue] = useState(
      associationValues?.post_in_website === false ? false : true
    );
    useEffect(() => {
      if (associationValues?.willing_to_associate_with_zynerd !== "No")
        handleAssociationValues(feedbackQuestions.association.key, "Yes");
      if (associationValues?.post_in_website !== false)
        handleAssociationValues(feedbackQuestions.association.checkKey, true);
    }, []);
    return (
      <div className="flex flex-col gap-10">
        <div className="flex flex-col gap-6 pt-2 pb-16">
          <div className="flex flex-col gap-1">
            <p className="text-lg font-extrabold">{feedbackQuestions.association.question}</p>
            <p className="text-sm text-[#5E5E5E]">{feedbackQuestions.association.subText}</p>
          </div>
          <div className="flex gap-2">
            {feedbackQuestions.association?.options?.map((option, index) => {
              return (
                <ChoiceContainer
                  index={index}
                  selectedKey={feedbackQuestions.association.key}
                  choiceValue={option}
                  selectedChoiceValues={associationValues}
                  handleSelectChoice={handleAssociationValues}
                />
              );
            })}
          </div>
        </div>
        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            className="mt-[10px]"
            checked={postCheckValue}
            maxLength={100}
            onChange={() => {
              handleAssociationValues(feedbackQuestions?.association?.checkKey, !postCheckValue);
              setPostCheckValue(!postCheckValue);
            }}
          />
          <p className="pt-1 font-bold">{feedbackQuestions.association.checkText}</p>
        </div>
      </div>
    );
  };

  const FeedbackWelcome = () => {
    return (
      <div className="flex flex-col w-full items-center justify-center">
        <div className="flex flex-col items-center justify-center gap-2">
          <p className="text-xl font-bold">{`Hey there! \u{1F44B}`}</p>
          <p className="text-sm text-[#5E5E5E] font-medium">
            Thank you for trusting ZyNerd to guide you through your NEET PG 2024 journey.
          </p>
        </div>
        <img
          src={images.feedbackWelcome}
          className={`w-[60%] lg:w-[50%] !max-w-[360px] aspect-square`}
        />
        <div className="flex flex-col w-full justify-center items-center gap-3">
          <div className="flex flex-col justify-center items-center gap-2">
            <p className="text-sm text-[#5E5E5E] font-medium">
              We’d love your feedback! Please take 5 minutes to fill out this quick survey.
            </p>
            <p className="text-sm text-[#5E5E5E] font-medium">
              Your responses are confidential and help us improve the platform.
            </p>
          </div>
          <div className="flex flex-col gap-3 items-center justify-center">
            <div className={`flex gap-3`}>
              <div
                className={`flex text-xs font-bold bg-[#F5F5F5] px-6 py-2 rounded  cursor-pointer`}
                onClick={() => {
                  submitFeedback("REMIND_LATER");
                  handleFeedbackPopupClose();
                }}
              >
                May Be Later
              </div>
              <div
                className={`flex !gap-2 text-xs font-bold bg-[#F46C3C] text-white pl-8 pr-5 py-2 rounded cursor-pointer`}
                onClick={() => {
                  setFeedbackStep("Portal");
                }}
              >
                Start
                <span className="flex items-center justify-center">
                  <ArrowRightIcon className="min-w-3 aspect-square stroke-2" />
                </span>
              </div>
            </div>
            <p
              className="flex items-center justify-center text-xs text-[#5E5E5E] hover:underline cursor-pointer"
              onClick={() => {
                submitFeedback("NOT_INTERESTED");
                handleFeedbackPopupClose();
              }}
            >
              I'm not interested
            </p>
          </div>
        </div>
      </div>
    );
  };

  const FeedbackPortal = () => {
    const [ratingValues, setRatingValues] = useState(feedbackData?.ratings || {});
    const [optionValues, setOptionValues] = useState(feedbackData?.options || {});
    const [otherValue, setOtherValue] = useState(feedbackOtherValue);

    const [textValues, setTextValues] = useState(feedbackData?.texts || {});
    const [associationValues, setAssociationValues] = useState(feedbackData?.association || {});
    const handleRatingValues = (key, rating) => {
      setRatingValues((prevRatings) => ({
        ...prevRatings,
        [key]: rating,
      }));
    };
    const handleOptionValues = (key, option) => {
      setOptionValues((prevOptions) => ({
        ...prevOptions,
        [key]: option,
      }));
    };
    const handleTextValues = (key, text) => {
      setTextValues((prevTexts) => ({
        ...prevTexts,
        [key]: text,
      }));
    };
    const handleAssociationValues = (key, text) => {
      setAssociationValues((prevAssociation) => ({
        ...prevAssociation,
        [key]: text,
      }));
    };
    const enableNextButton = () => {
      if (currentPortalStep === "ratings" && !Object.values(ratingValues).length > 0) return false;
      else if (currentPortalStep === "options" && !Object.values(optionValues).length > 0)
        return false;
      else {
        return true;
      }
    };
    const handleNextClick = () => {
      switch (currentPortalStep) {
        case "ratings":
          handleFeedbackData("ratings", ratingValues);
          setCurrentPortalStep("options");
          break;
        case "options":
          handleFeedbackData("options", optionValues);
          if (otherValue) setFeedbackOthervalue(otherValue);
          setCurrentPortalStep("texts");
          break;
        case "texts":
          handleFeedbackData("texts", textValues);
          setCurrentPortalStep("association");
          break;
        case "association":
          let tempAssociationValues = associationValues;
          if (associationValues.willing_to_associate_with_zynerd === "Yes")
            tempAssociationValues["willing_to_associate_with_zynerd"] = true;
          else tempAssociationValues["willing_to_associate_with_zynerd"] = false;
          handleFeedbackData("association", tempAssociationValues);
          if (feedbackOtherValue) {
            let modifiedOptionValues = { ...optionValues };
            modifiedOptionValues["how_you_know"] = feedbackOtherValue;
            handleFeedbackData("options", modifiedOptionValues);
          }
          setFeedbackStep("ThankYou");
          break;
        default:
          console.error("Invalid option");
          break;
      }
    };
    const handleBackClick = () => {
      switch (currentPortalStep) {
        case "options":
          handleFeedbackData("options", optionValues);
          if (otherValue) setFeedbackOthervalue(otherValue);
          setCurrentPortalStep("ratings");
          break;
        case "texts":
          handleFeedbackData("texts", textValues);
          setCurrentPortalStep("options");
          break;
        case "association":
          handleFeedbackData("association", associationValues);
          setCurrentPortalStep("texts");
          break;
        default:
          console.error("Invalid option");
          break;
      }
    };
    const handleOtherValueChange = (e) => {
      setOtherValue(e.target.value);
    };
    return (
      <div className="flex flex-col w-full h-full items-center gap-8 my-4">
        <p className="text-center text-lg font-bold">
          Please rate your service experience for the following parameters
        </p>
        <div className="flex flex-col items-center justify-center w-full max-w-[70%] h-full">
          <ProgressBar />
          <div className="flex h-full w-full max-h-[52vh] overflow-auto pb-24 mb-10">
            {currentPortalStep === "ratings" && (
              <FeedbackRatings
                ratingValues={ratingValues}
                handleRatingValues={handleRatingValues}
              />
            )}
            {currentPortalStep === "options" && (
              <FeedbackOptions
                optionValues={optionValues}
                handleOptionValues={handleOptionValues}
                otherValue={otherValue}
                handleOtherValueChange={handleOtherValueChange}
              />
            )}
            {currentPortalStep === "texts" && (
              <FeedbackTexts textValues={textValues} handleTextValues={handleTextValues} />
            )}
            {currentPortalStep === "association" && (
              <FeedbackAssociation
                associationValues={associationValues}
                handleAssociationValues={handleAssociationValues}
              />
            )}
          </div>
        </div>
        <div
          className={`${styles.footer} flex ${
            currentPortalStep === "ratings" ? "justify-end" : "justify-between"
          } px-6 py-2`}
        >
          {currentPortalStep !== "ratings" && (
            <div
              className={`flex text-xs font-bold text-[#000] px-6 py-2 gap-1 cursor-pointer`}
              onClick={() => handleBackClick()}
            >
              <span className="flex items-center justify-center">
                <ChevronLeftIcon className="text-base min-w-3 min-h-3 stroke-2" />
              </span>
              Back
            </div>
          )}
          <div
            className={`text-xs font-bold text-white px-6 py-2 rounded  ${
              enableNextButton() ? "bg-[#F46C3C] cursor-pointer" : "bg-[#D9D9D9] cursor-not-allowed"
            }`}
            onClick={() => (enableNextButton() ? handleNextClick() : null)}
          >
            {currentPortalStep === "association" ? "Finish" : "Next"}
          </div>
        </div>
      </div>
    );
  };

  const FeedbackThankYou = () => {
    return (
      <div className="flex flex-col w-full items-center justify-center px-24 pt-10 pb-20">
        <img
          src={images.feedbackThankYou}
          className={`w-[80%] lg:w-[60%] !max-w-[360px] aspect-square`}
        />
        <div className="flex flex-col w-full justify-center items-center gap-4">
          <div className="flex flex-col justify-center items-center gap-2">
            <p className="text-2xl font-extrabold">Thank you for your feedback</p>
            <p className="text-sm text-[#5E5E5E] text-center text-wrap max-w-[400px]">
              Help others understand what makes us special! A quick Google review sharing your
              experience would be fantastic and really help future doctors.
            </p>
          </div>
          <div className={` flex gap-3`}>
            <div
              className={`flex text-xs font-bold bg-[#F5F5F5] px-6 py-2 rounded  cursor-pointer`}
              onClick={() => handleFeedbackPopupClose()}
            >
              Close
            </div>

            <div
              className={`flex !gap-2 text-xs font-bold bg-[#F46C3C] text-white pl-5 pr-3 py-2 rounded cursor-pointer`}
              onClick={() => {
                window.open(
                  "https://www.google.com/maps/place//data=!4m3!3m2!1s0x3b04f1e19ff667c5:0x97074d8615806cd7!12e1?source=g.page.m._&laa=merchant-review-solicitation",
                  "_blank"
                );
              }}
            >
              Review on Google
              <span className="flex items-center justify-center">
                <ArrowUpRightIcon className="min-w-3 aspect-square stroke-2" />
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <CommonModal show={showFeedback} handleClose={handleCloseFeedback} center={true} size="976px">
      <div id="feedback-dialog" className={`bg-white flex-col rounded-2xl`}>
        <div
          id="feedback-title"
          className="!p-3 flex items-center justify-between pop-header rounded-t-2xl bg-white "
        >
          <p className="flex font-bold gap-2">
            <span>🚀</span> <span>Help us improve ZyNerd</span>
          </p>
        </div>
        <div id="feedback-body" className={`flex h-auto min-h-[600px] w-full`}>
          {feedbackStep === "Welcome" && <FeedbackWelcome />}
          {feedbackStep === "Portal" && <FeedbackPortal />}
          {feedbackStep === "ThankYou" && <FeedbackThankYou />}
        </div>
      </div>
    </CommonModal>
  );
};
export default Feedback;

export const ChoiceContainer = ({
  index,
  selectedKey,
  choiceValue,
  selectedChoiceValues,
  handleSelectChoice,
}) => {
  const controlProps = (item) => ({
    key: item,
    checked: selectedChoiceValues[selectedKey] === item,
    onChange: () => handleSelectChoice(selectedKey, item),
    value: item,
    name: "color-radio-button-demo",
    inputProps: { "aria-label": item },
  });

  return (
    <div
      key={`${choiceValue}-${index}`}
      className={`w-full min-w-[324px] !px-3 !py-3 flex items-center justify-start border  rounded-lg !gap-2 cursor-pointer ${
        selectedChoiceValues[selectedKey] === choiceValue
          ? "bg-[#FFF8F5] !border-[#F46C3C]"
          : "bg-white !border-[#D9D9D9]"
      }`}
      onClick={() => handleSelectChoice(selectedKey, choiceValue)}
    >
      <Radio
        {...controlProps(choiceValue)}
        sx={{
          color: "#D9D9D9",
          "&.Mui-checked": {
            color: "#F46C3C",
          },
          transform: "scale(0.75)",
          padding: "0px",
        }}
      />
      <span className="flex items-center text-sm font-medium">{choiceValue}</span>
    </div>
  );
};
